// CANONICAL Annual Academic & Learning Record renderer.
//
// This file is the single source of truth. It is copied verbatim into each
// panel by `Scripts/sync_report_renderers.py`; do not edit the panel copies.
// The same report version must render the same facts for Admin, Teacher,
// Student, and Parent, so any change here must be synced to all four.
import React, { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, FileText, Loader2, Printer, X } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  date,
  humanise,
  list,
  number,
  outOf,
  percentage,
  safeName,
  text,
  words,
} from "./reportFormat";
import { PAGE_BUDGET, paginate, partLabel } from "./reportPaging";
import { useViewerInset } from "./useViewerInset";
import "./officialGrowthAchievementPrint.css";

// Assignments are graded with a qualitative rubric, never a derived number.
const proficiency = row => {
  const levels = list(row.proficiency);
  if (levels.length) return levels.map(item => `${humanise(item.criterion)}: ${text(item.level)}`).join("; ");
  return outOf(row.achieved_marks, row.maximum_marks, "Not assessed");
};

const scoring = row => row.verification === "teacher_verified" || row.verified ? "Teacher verified"
  : row.verification === "system_scored" ? "Scored by the platform"
  : "Not verified";

function Heading({ number: order, title, text: description }) { return <div className="ga-section-heading"><div><span>{order}</span><h2>{title}</h2></div><p>{description}</p></div>; }
function Metric({ label, value, note }) { return <div className="ga-metric"><span>{label}</span><strong>{value ?? "Not recorded"}</strong><small>{note}</small></div>; }
function RecordTable({ rows, columns, empty }) {
  if (!rows.length) return <p className="ga-empty-copy">{empty}</p>;
  return (
    <table className="ga-table ga-record-table">
      <thead><tr>{columns.map(column => <th key={column.label}>{column.label}</th>)}</tr></thead>
      <tbody>{rows.map((row, index) => <tr key={row.source_id || row.id || index}>{columns.map(column => <td key={column.label}>{column.render(row)}</td>)}</tr>)}</tbody>
    </table>
  );
}

/**
 * NCRF credits recorded for the academic year.
 *
 * Credits are qualification-bearing, so only what the school recorded appears
 * here. An unrecorded domain is named as unrecorded rather than shown as zero,
 * and no level or credit is ever derived.
 */
function NcrfSection({ ncrf, number: order }) {
  if (!ncrf || ncrf.state !== "recorded") return null;
  const rows = list(ncrf.rows);
  return (
    <div className="ga-block">
      <Heading
        number={String(order).padStart(2, "0")}
        title="NCRF credits"
        text={`Recorded National Credit Framework results for the ${ncrf.stage || "recorded"} stage. Credits are shown exactly as the school recorded them.`}
      />
      <div className="ga-metrics">
        <Metric label="Credits earned" value={number(ncrf.credits_earned, "Not recorded")} note="Across recorded domains" />
        <Metric label="Credits possible" value={number(ncrf.credits_possible, "Not recorded")} note="Sum of recorded maximums" />
        {/* Only recorded domains contribute, so this is not whole-stage progress. */}
        <Metric label="Attainment" value={percentage(ncrf.completion_percentage, "Not calculated")} note="Recorded domains only" />
        <Metric label="Domains recorded" value={`${number(ncrf.domains_recorded, "0")} / ${number(ncrf.domains_expected, "—")}`} note="For this NCRF stage" />
      </div>
      {list(ncrf.domains_missing).length > 0 && (
        <section className="ga-data-note has-gaps">
          <h3>Domains without a recorded credit</h3>
          <p>{list(ncrf.domains_missing).join(", ")}. Attainment above covers recorded domains only and is not whole-stage progress.</p>
        </section>
      )}
      <RecordTable
        rows={rows}
        columns={[
          { label: "NCRF domain", render: row => text(row.domain) },
          { label: "Level", render: row => words(row.level) },
          { label: "Credits", render: row => outOf(row.credits_earned, row.max_credits, "Not recorded") },
          { label: "Score", render: row => percentage(row.score_percentage) },
          { label: "Assessed", render: row => date(row.assessed_at) },
        ]}
        empty="No NCRF credits were recorded for this academic year."
      />
    </div>
  );
}

function SourceSummary({ context }) {
  const summary = context.summary || {};
  const cards = [
    ["Lessons", summary.planned_lessons || 0, "planned learning records"],
    ["Assignments", `${summary.assignments_submitted || 0} / ${summary.assignments || 0}`, "submissions recorded"],
    ["Quizzes", summary.quiz_attempts || 0, "attempts recorded"],
    ["Oral learning", summary.oral_activities || 0, "results recorded"],
    ["Assessments", summary.formal_assessments || 0, "included results"],
  ];
  return <div className="ga-context-sources">{cards.map(([label, value, note]) => <div key={label}><small>{label}</small><strong>{value}</strong><span>{note}</span></div>)}</div>;
}

function contextSections(context) {
  return [
    {
      heading: "Lessons and planned learning",
      description: "Planned teaching recorded for this class context during the academic year.",
      rows: list(context.lessons),
      columns: [
        { label: "Lesson / activity", render: row => text(row.title) },
        { label: "Subject", render: row => text(row.subject) },
        { label: "Learning outcome", render: row => text(row.outcome) },
        { label: "Date", render: row => date(row.date || row.event_at) },
      ],
      empty: "No lesson-plan coverage was recorded for this class context.",
    },
    {
      heading: "Assignments and submissions",
      description: "Assignment work, its submission state, and the teacher's recorded proficiency.",
      rows: list(context.assignments),
      columns: [
        { label: "Assignment", render: row => text(row.title) },
        { label: "Subject", render: row => text(row.subject) },
        { label: "Submission", render: row => words(row.status) },
        { label: "Proficiency", render: row => proficiency(row) },
        { label: "Teacher feedback", render: row => text(row.feedback) },
      ],
      empty: "No assignment records were found for this class context.",
    },
    {
      heading: "Normal quiz attempts",
      description: "Course quiz attempts completed by the student, with how each was scored.",
      rows: list(context.normal_quizzes),
      columns: [
        { label: "Quiz", render: row => text(row.title) },
        { label: "Subject", render: row => text(row.subject) },
        { label: "Result", render: row => percentage(row.percentage) },
        { label: "Scoring", render: row => scoring(row) },
        { label: "Completed", render: row => date(row.completed_at || row.date) },
      ],
      empty: "No normal quiz attempts were recorded for this class context.",
    },
    {
      heading: "Oral learning activities",
      description: "Oral and short-answer rounds recorded against the student.",
      rows: list(context.oral_activities),
      columns: [
        { label: "Activity", render: row => text(row.title) },
        { label: "Subject", render: row => text(row.subject) },
        { label: "Result", render: row => percentage(row.percentage) },
        { label: "Scoring", render: row => scoring(row) },
        { label: "Completed", render: row => date(row.completed_at || row.date) },
      ],
      empty: "No oral learning records were found for this class context.",
    },
    {
      heading: "Formal assessments",
      description: "School-verified, policy-mapped assessment outcomes included in this record.",
      rows: list(context.formal_assessments),
      columns: [
        { label: "Assessment", render: row => text(row.assessment_title || row.title) },
        { label: "Subject / type", render: row => `${text(row.subject)} · ${humanise(row.type)}` },
        { label: "Date", render: row => date(row.event_at) },
        { label: "Achieved", render: row => outOf(row.achieved_marks, row.maximum_marks) },
        { label: "Feedback", render: row => text(row.teacher_feedback || row.feedback) },
      ],
      empty: "No verified formal assessment outcomes were recorded for this class context.",
    },
    {
      heading: "Excluded or incomplete sources",
      description: "Records retained for visibility, with the reason each was not counted.",
      rows: list(context.excluded_records),
      columns: [
        { label: "Source", render: row => text(row.title || humanise(row.source_type)) },
        { label: "Subject", render: row => text(row.subject) },
        { label: "Reason", render: row => text(row.reason) },
      ],
      empty: "No incomplete sources were recorded for this class context.",
    },
  ];
}

export default function OfficialAcademicTranscriptDocument({ report, onClose }) {
  const root = useRef(null);
  const [exporting, setExporting] = useState(false);
  const { anchorRef, style: insetStyle } = useViewerInset();

  const student = report.student_snapshot || report.student || {};
  const source = report.source_snapshot || {};
  const rawContexts = report.class_contexts ?? source.class_contexts;
  // A version issued before the class-by-class format has no `class_contexts`
  // field at all. An empty array means the current format found no records —
  // a different thing, which must not be described as a historical version.
  const isHistorical = !Array.isArray(rawContexts);
  const contexts = list(rawContexts);
  const school = report.school_branding_snapshot?.school_name || "Edumonics School";
  const overview = report.academic_summary || source.academic_summary || report.coverage_summary || {};
  const participation = overview.learning_participation || report.coverage_summary || source.coverage_summary || overview;
  const gaps = list(report.data_gaps || source.data_gaps);

  // NCRF credits are academic-year facts rather than class-scoped ones, so they
  // take their own page between the overview and the class journeys. The page
  // appears only when the school actually recorded credits.
  const ncrf = report.ncrf || source.ncrf || null;
  const hasNcrf = Boolean(ncrf && ncrf.state === "recorded");
  const ncrfSectionNumber = hasNcrf ? 3 : null;

  // The source map opens the context's first appendix page rather than taking a
  // page of its own — on its own it filled barely a quarter of an A4 sheet.
  const SUMMARY_COST = 10;
  const contextPages = useMemo(() => {
    // Sections are numbered in one running sequence across the whole document,
    // continuing from the overview sections and the optional NCRF section.
    let section = hasNcrf ? 3 : 2;
    return contexts.flatMap((context, contextIndex) => {
      const paged = paginate(contextSections(context), PAGE_BUDGET, SUMMARY_COST);
      const pages = paged.length ? paged : [[]];
      return pages.map((blocks, index) => ({
        kind: "appendix",
        context,
        contextIndex,
        lead: index === 0,
        summaryNumber: index === 0 ? (section += 1) : null,
        blocks: blocks.map(block => ({ ...block, number: block.part === 0 ? (section += 1) : section })),
      }));
    });
  }, [contexts, hasNcrf]);

  const ncrfPageOffset = hasNcrf ? 1 : 0;
  const totalPages = 3 + ncrfPageOffset + contextPages.length;

  const exportPdf = async () => {
    const pages = Array.from(root.current?.querySelectorAll(".ga-pdf-page") || []);
    if (!pages.length || exporting) return;
    setExporting(true);
    try {
      if (document.fonts?.ready) await document.fonts.ready;
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
      for (let index = 0; index < pages.length; index += 1) {
        const canvas = await html2canvas(pages[index], { scale: 2.2, useCORS: true, backgroundColor: "#fff", logging: false });
        if (index) pdf.addPage("a4", "portrait");
        pdf.addImage(canvas.toDataURL("image/jpeg", 0.96), "JPEG", 0, 0, 210, 297, undefined, "FAST");
      }
      pdf.save(`${safeName(student.name)}_Annual_Academic_Learning_Record_${safeName(report.academic_year)}_v${report.version || 1}.pdf`);
    } catch (error) { console.error("Annual Academic & Learning Record PDF export failed", error); window.alert("The official PDF could not be prepared. Please try again."); }
    finally { setExporting(false); }
  };

  const Footer = ({ page }) => <div className="ga-page-footer"><span>Annual Academic &amp; Learning Record · Version {report.version || 1}</span><span>{String(page).padStart(2, "0")} / {String(totalPages).padStart(2, "0")}</span></div>;
  const contextName = (context, index) => context.class_snapshot?.name || context.name || `Class context ${index + 1}`;
  const contextScope = context => `Class ${context.class_snapshot?.grade || context.grade || "Not recorded"}${(context.class_snapshot?.section || context.section) ? ` · ${context.class_snapshot?.section || context.section}` : ""}`;

  return (
    <>
      {/* Measures the content column so the viewer never covers the sidebar. */}
      <span ref={anchorRef} aria-hidden="true" />
      {createPortal(
        <div className="official-report-overlay" style={insetStyle}>
          <article className="official-report-paper">
            <div className="official-report-toolbar print:hidden">
              <div className="ga-toolbar-identity">
                {onClose && <button type="button" onClick={onClose} className="ga-toolbar-back" aria-label="Close report"><X size={20} /></button>}
                <div className="ga-toolbar-icon"><FileText size={22} /></div>
                <div><strong>Annual Academic &amp; Learning Record</strong><span>{student.name || "Student"} · Version {report.version || 1} · {words(report.status || "draft")}</span></div>
              </div>
              <div className="ga-toolbar-actions">
                <button type="button" onClick={() => window.print()} className="ga-secondary-button"><Printer size={16} />Print</button>
                <button type="button" disabled={exporting} onClick={exportPdf} className="ga-primary-button">{exporting ? <Loader2 size={16} className="ga-spin" /> : <Download size={16} />}{exporting ? "Generating…" : "Export as PDF"}</button>
              </div>
            </div>

            <div ref={root} className="official-report-document">
              <section className="ga-pdf-page ga-page-cover">
                <div className="ga-cover-ribbon"><span>{school}</span><span>Official student report</span></div>
                <div className="ga-cover-centre">
                  <p>Annual Academic &amp; Learning Record</p>
                  <h1>{student.name || "Student report"}</h1>
                  <div className="ga-cover-rule" />
                  <div className="ga-cover-details">
                    <div><small>Class and section</small><strong>Class {student.grade || "Not recorded"}{student.section ? ` · ${student.section}` : ""}</strong></div>
                    <div><small>Academic year</small><strong>{report.academic_year || "Not recorded"}</strong></div>
                    <div><small>Record status</small><strong>{words(report.status || "draft")}</strong></div>
                  </div>
                </div>
                <div className="ga-cover-bottom"><span>Summary and detailed learning appendix</span><span>Issued {date(report.published_at || report.generated_at)}</span></div>
              </section>

              <section className="ga-pdf-page ga-page-profile">
                <div className="ga-page-running"><span>{school}</span><span>Academic-year overview · {report.academic_year}</span></div>
                <Heading number="01" title="Academic-year overview" text="A class-by-class record of saved learning activity and verified outcomes." />
                <div className="ga-metrics">
                  <Metric label="Class contexts" value={number(overview.class_context_count ?? participation.class_contexts ?? contexts.length, "0")} note="Class / section journeys" />
                  <Metric label="Lessons planned" value={number(participation.planned_lessons, "0")} note="Learning coverage recorded" />
                  <Metric label="Assignments" value={number(participation.assignments, "0")} note={`${number(participation.assignments_submitted, "0")} submission(s)`} />
                  <Metric label="Learning checks" value={number((participation.normal_quizzes || 0) + (participation.oral_activities || 0) + (participation.formal_assessments || 0), "0")} note="Quizzes, oral work and assessments" />
                </div>
                <div className="ga-section-spacer" />
                <Heading number="02" title="Verified subject outcomes" text="Only school-verified, policy-mapped assessment results are counted." />
                <RecordTable
                  rows={list(report.subject_summaries)}
                  columns={[
                    { label: "Subject", render: row => text(row.subject) },
                    { label: "Assessments", render: row => number(row.assessment_count, "0") },
                    { label: "Achieved / possible", render: row => outOf(row.achieved_marks, row.maximum_marks) },
                    { label: "Performance", render: row => percentage(row.percentage, "Not calculated") },
                    { label: "Grade", render: row => row.grade?.label || "Not configured" },
                  ]}
                  empty="No verified, policy-mapped subject results were recorded for this academic year."
                />
                {isHistorical
                  ? <section className="ga-data-note has-gaps"><h3>Historical summary-only version</h3><p>This retained version was issued before the class-by-class appendix format and cannot be expanded. It remains unchanged and valid as issued. To obtain the detailed learning journey, issue a new Annual Academic &amp; Learning Record.</p></section>
                  : contexts.length ? null
                  : <section className="ga-data-note has-gaps"><h3>No class context recorded</h3><p>This record was generated in the current detailed format, but no class or section enrolment was found for the selected academic year. Confirm the student's class enrolment, then issue a new version.</p></section>}
                <Footer page={2} />
              </section>

              {hasNcrf && (
                <section className="ga-pdf-page ga-ncrf-page">
                  <div className="ga-page-running"><span>{school}</span><span>NCRF credits · {report.academic_year}</span></div>
                  <NcrfSection ncrf={ncrf} number={ncrfSectionNumber} />
                  <Footer page={3} />
                </section>
              )}

              {contextPages.map((page, pageIndex) => {
                const pageNumber = pageIndex + 3 + ncrfPageOffset;
                const label = contextName(page.context, page.contextIndex);
                const scope = contextScope(page.context);

                return (
                  <section className="ga-pdf-page ga-appendix-page" key={`appendix-${page.contextIndex}-${pageIndex}`}>
                    <div className="ga-page-running"><span>{school}</span><span>{label} · {page.lead ? "learning journey" : "academic appendix"}</span></div>
                    {page.lead && (
                      <div className="ga-block">
                        <Heading number={String(page.summaryNumber).padStart(2, "0")} title={scope} text="A source map for this class and section, followed by every saved record in it." />
                        <SourceSummary context={page.context} />
                        {list(page.context.excluded_records).length
                          ? <section className="ga-data-note has-gaps"><h3>Data gaps to review</h3><p>{list(page.context.excluded_records).length} source record(s) need attention and are listed with their reason.</p></section>
                          : <section className="ga-data-note"><h3>Data completeness</h3><p>No excluded source records were found for this class context.</p></section>}
                      </div>
                    )}
                    {page.blocks.map((block, blockIndex) => (
                      <div className="ga-block" key={`${block.heading}-${block.part}-${blockIndex}`}>
                        <Heading
                          number={String(block.number).padStart(2, "0")}
                          title={`${block.heading}${partLabel(block)}`}
                          text={`${scope} · ${block.description}`}
                        />
                        <RecordTable rows={block.rows} columns={block.columns} empty={block.empty} />
                      </div>
                    ))}
                    <Footer page={pageNumber} />
                  </section>
                );
              })}

              <section className="ga-pdf-page ga-page-conclusion">
                <div className="ga-page-running"><span>{school}</span><span>Academic summary and release</span></div>
                <Heading number="Final" title="Academic summary and care" text="This record reports saved evidence. It never infers missing outcomes or school decisions." />
                <div className="ga-insight-grid">
                  <section className="ga-insight"><h3>Teacher comment</h3><p>{report.teacher_comment || "A teacher comment has not been added to this report version."}</p></section>
                  <section className="ga-insight"><h3>Learning support</h3><p>{report.support_note || "No additional learning support note has been recorded."}</p></section>
                </div>
                <section className={`ga-data-note${gaps.length ? " has-gaps" : ""}`}>
                  <h3>{gaps.length ? "Data availability and care" : "Record methodology"}</h3>
                  {gaps.length ? <ul>{gaps.map((gap, index) => <li key={index}>{text(gap)}</li>)}</ul> : <p>All available saved sources for the academic year were included. Any future incomplete records remain visibly excluded rather than estimated.</p>}
                </section>
                <div className="ga-release">
                  <div><small>Prepared by</small><strong>{report.generated_by_name || "School reporting system"}</strong></div>
                  <div><small>Authorised signatory</small><strong>{report.policy_snapshot?.signatory_name || report.policy_snapshot?.signatory_title || "Authorised Signatory"}</strong></div>
                  <div><small>Issue date</small><strong>{date(report.published_at || report.generated_at)}</strong></div>
                </div>
                <Footer page={totalPages} />
              </section>
            </div>
          </article>
        </div>,
        document.body
      )}
    </>
  );
}
